import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ShieldCheck,
  Lock,
  Heart,
  CheckCircle2,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const STRIPE_DONATION_LINK = 'https://buy.stripe.com/3cI28jaj42tvghX7xigw002';

const INITIATIVE_CONFIG = {
  hpc: {
    key: 'hpc',
    title: 'His Presence Chapel Inc.',
    shortTitle: 'HPC',
    lead: 'You are about to make a secure donation that supports hope, compassionate service, and sustainable community impact.',
    color: '#F15A24',
    soft: 'rgba(241,90,36,0.10)',
    points: [
      'Secure payment processing with Stripe',
      'Fast and trusted donation experience',
      'Support for meaningful community impact',
    ],
  },
  seeal: {
    key: 'seeal',
    title: 'SEEAL Africa',
    shortTitle: 'SEEAL Africa',
    lead: 'You are about to make a secure donation that supports empowerment, enterprise, and opportunity-building across communities.',
    color: '#0B5FA5',
    soft: 'rgba(11,95,165,0.10)',
    points: [
      'Secure payment processing with Stripe',
      'Fast and trusted donation experience',
      'Support for practical empowerment programs',
    ],
  },
  rahvai: {
    key: 'rahvai',
    title: 'RAHVAI',
    shortTitle: 'RAHVAI',
    lead: 'You are about to make a secure donation that supports humanitarian relief, dignity, resilience, and hope for vulnerable communities.',
    color: '#2D3E55',
    soft: 'rgba(45,62,85,0.10)',
    points: [
      'Secure payment processing with Stripe',
      'Fast and trusted donation experience',
      'Support for urgent and long-term humanitarian impact',
    ],
  },
};

export default function DonateCheckoutPage() {
  const [searchParams] = useSearchParams();
  const initiativeParam = (searchParams.get('initiative') || 'hpc').toLowerCase();
  const initiative = INITIATIVE_CONFIG[initiativeParam] || INITIATIVE_CONFIG.hpc;

  const stripeCheckoutLink = useMemo(() => {
    const url = new URL(STRIPE_DONATION_LINK);
    url.searchParams.set('prefilled_promo_code', '');
    return url.toString();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      <section className="pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-stretch">
              <Card className="rounded-3xl border-0 shadow-xl overflow-hidden">
                <CardContent className="p-10 h-full flex flex-col justify-between">
                  <div>
                    <div
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                      style={{
                        backgroundColor: initiative.soft,
                        color: initiative.color,
                      }}
                    >
                      <Heart size={16} />
                      Secure Donation Checkout
                    </div>

                    <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#2D3E55] mb-5 leading-tight">
                      Support {initiative.shortTitle}
                    </h1>

                    <p className="text-lg text-slate-600 leading-relaxed mb-8">
                      {initiative.lead}
                    </p>

                    <div className="space-y-4 mb-8">
                      {initiative.points.map((point) => (
                        <div key={point} className="flex items-start gap-3">
                          <CheckCircle2
                            size={20}
                            className="mt-0.5"
                            style={{ color: initiative.color }}
                          />
                          <p className="text-slate-600">{point}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <a href={stripeCheckoutLink} target="_blank" rel="noopener noreferrer">
                      <Button
                        size="lg"
                        className="h-14 px-8 text-white text-base font-semibold"
                        style={{ backgroundColor: initiative.color }}
                      >
                        Continue to Stripe
                        <ArrowRight size={18} className="ml-2" />
                      </Button>
                    </a>

                    <Link to={`/donate?initiative=${initiative.key}`}>
                      <Button
                        size="lg"
                        variant="outline"
                        className="h-14 px-8 border-[#2D3E55] text-[#2D3E55] text-base font-semibold"
                      >
                        Back to Donation Page
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-3xl border-0 shadow-xl bg-[#2D3E55] text-white overflow-hidden">
                <CardContent className="p-10 h-full flex flex-col justify-between">
                  <div>
                    <h2 className="font-heading text-3xl font-semibold mb-6">
                      Donation Summary
                    </h2>

                    <div className="space-y-5">
                      <div className="rounded-2xl bg-white/10 border border-white/10 p-5">
                        <p className="text-white/70 text-sm mb-1">Initiative</p>
                        <p className="text-xl font-semibold">{initiative.title}</p>
                      </div>

                      <div className="rounded-2xl bg-white/10 border border-white/10 p-5">
                        <div className="flex items-start gap-3">
                          <ShieldCheck size={22} className="mt-0.5" />
                          <div>
                            <p className="font-semibold mb-1">Protected Payment Flow</p>
                            <p className="text-white/75 text-sm leading-relaxed">
                              Your donation is handled through Stripe for a secure, professional checkout experience.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-white/10 border border-white/10 p-5">
                        <div className="flex items-start gap-3">
                          <Lock size={22} className="mt-0.5" />
                          <div>
                            <p className="font-semibold mb-1">Trusted Processing</p>
                            <p className="text-white/75 text-sm leading-relaxed">
                              You will be redirected to Stripe to complete your donation safely and confidently.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10">
                    <a href={stripeCheckoutLink} target="_blank" rel="noopener noreferrer">
                      <Button
                        size="lg"
                        className="w-full h-14 text-base font-semibold text-[#2D3E55] bg-white hover:bg-white/90"
                      >
                        Donate Now
                        <ArrowRight size={18} className="ml-2" />
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}